import { format } from "date-fns";

import { UserType } from "src/types/users";
import {
  AddCaseProps,
  // CaseDoctorIOveraProps,
  CaseDoctorProps,
  InsuranceAttributesProps,
  PatientAddress,
  PatientAttributes,
  PatientPhoneNumber,
  PrescriptionAttributesType,
  ProcedureProps,
} from "src/pages/addCase/types";
import { CaseCloneResponseType } from "src/pages/cloneCase/types";



export function populatingCloneCase(
  { data }: CaseCloneResponseType,
  userData: UserType,
): AddCaseProps {
  const caseFormData: AddCaseProps = {} as AddCaseProps;

  if (data.case) {
    caseFormData.mos = "online";
    caseFormData.user_id = userData ? userData.id : 0;
    caseFormData.creator_name = userData ? userData.name : "";
    caseFormData.submission_source = data.case.submission_source || "provider";

    if (data.case.category && ["med", "rx", "medrx"].includes(data.case.category)) {
      caseFormData.category = data.case.category;
    } else {
      if (data.prescription.product.includes("iovera")) {
        caseFormData.category = "medrx";
      } else {
        caseFormData.category = ["med", "rx", "medrx"].includes(data.case?.category ?? "") ? data.case?.category : "med";
      }
    }

    caseFormData.prescription_attributes = {} as PrescriptionAttributesType;
  }

  if (data.procedure && data.procedure.length > 0) {
    caseFormData.procedure_attributes = {} as ProcedureProps;
    caseFormData.procedure_attributes.cpt_code = data.procedure[0].cpt_code;
  }

  if (!data.prescription.product.includes("iovera")) {
    if (data.doctor) {
      caseFormData.doctor_firstlastname = `${data.doctor.firstname} ${data.doctor.lastname}`;
      caseFormData.doctor_fullname = `${data.doctor.firstname} ${
        data.doctor.middlename || ""
      } ${data.doctor.lastname}`;
    }
  
    if (data.practice || data.practice_address) {
      caseFormData.practice_name = `${data.practice.name} - ${data.practice_address.full_address}`;
    }

    caseFormData.doctor_attributes = {
      practice_id: data.doctor.practice_id,
      global_doctor_id: data.doctor.global_doctor_id,
    } as CaseDoctorProps;
  }

  caseFormData.patient_attributes = {} as PatientAttributes;

  if (data.patient && !data.prescription.product.includes("iovera")) {
    const { patient, patient_attributes } = data;

    caseFormData.patient_firstlastname = `${patient.firstname} ${patient.lastname}`;
    caseFormData.patient_fullname = `${patient.firstname} ${
      patient.middlename || ""
    } ${patient.lastname}`;
    caseFormData.patient_dob = patient.dob;

    const patient_attr: { is_us_citizen: boolean | null; is_enrolled_in_federal: boolean | null; } = {
      is_us_citizen: null, is_enrolled_in_federal: null,
    }
    if (patient_attributes && patient_attributes.length > 0) {
      if (patient_attributes.find((val) => val.name === "is_us_citizen"))
        patient_attr.is_us_citizen = patient_attributes.find((val) => val.name === "is_us_citizen")?.value === "true" ? true : false
      if (patient_attributes.find((val) => val.name === "is_enrolled_in_federal"))
        patient_attr.is_enrolled_in_federal = patient_attributes.find((val) => val.name === "is_enrolled_in_federal")?.value === "true" ? true : false
    }

    caseFormData.patient_attributes = {
      global_patient_id: patient.global_patient_id,
      title: patient.title || "",
      firstname: patient.firstname,
      middlename: patient.middlename || "",
      lastname: patient.lastname,
      gender: patient.gender,
      dob: patient.dob,
      ssn: patient.ssn || "",
      best_time_to_contact: patient.best_time_to_contact || "",
      patient_email_id: null,
      patient_email: data.email_address ? data.email_address.email : "",
      ...patient_attr
    } as PatientAttributes;
  };

  if (!data.prescription.product.includes("iovera") && data.patient_phone_numbers && data.patient_phone_numbers.length > 0) {
    const phone_numbers = [] as PatientPhoneNumber[];

    data.patient_phone_numbers.forEach((phone, index) => {
      phone_numbers[index] =
        {} as PatientPhoneNumber;
      phone_numbers[index].number =
        phone.number.toString();
      phone_numbers[index].phone_type =
        phone.phone_type || "cell";
      phone_numbers[
        index
      ].is_ok_to_leave_message = phone.is_ok_to_leave_message;
      phone_numbers[index].is_preferred =
        phone.is_preferred;
      phone_numbers[index].id = phone.id;
    });

    caseFormData.patient_attributes = {
      ...caseFormData.patient_attributes,
      phone_numbers,
    }
  }

  const address = {
    address_name: "home",
  } as PatientAddress;

  if (!data.prescription.product.includes("iovera") && data.patient_address) {
    address.street =
      data.patient_address.street || "";
    address.suite =
      data.patient_address.suite || "";
    address.city =
      data.patient_address.city || "";
    address.state =
      data.patient_address.state || "";
    address.zip =
      data.patient_address.zip || "";

    caseFormData.patient_attributes = {
      ...caseFormData.patient_attributes,
      address,
    }
  }

  if (!data.prescription.product.includes("iovera") && data.patient_entities_address) {
    (caseFormData.patient_attributes as PatientAttributes).address.address_name =
      data.patient_entities_address.address_name || "";
  }

  caseFormData.insurances_attributes = [{} as InsuranceAttributesProps];
  if (!data.prescription.product.includes("iovera") && data.insurances && data.insurances.length > 0) {
    data.insurances.forEach((insurance, index) => {
      if (caseFormData.insurances_attributes) {
        caseFormData.insurances_attributes[index] =
          {} as InsuranceAttributesProps;
        caseFormData.insurances_attributes[index].insurance_name =
          insurance.insurance_name;
        caseFormData.insurances_attributes[index].relation_to_patient =
          insurance.relation_to_patient;
        caseFormData.insurances_attributes[index].relation_firstname =
          insurance.relation_firstname || "";
        caseFormData.insurances_attributes[index].relation_middlename =
          insurance.relation_middlename || "";
        caseFormData.insurances_attributes[index].relation_lastname =
          insurance.relation_lastname || "";
        caseFormData.insurances_attributes[index].policy =
          insurance.policy || "";
        caseFormData.insurances_attributes[index].group_number =
          insurance.group_number || "";
        caseFormData.insurances_attributes[index].insurance_number =
          insurance.insurance_number || "";
        caseFormData.insurances_attributes[index].member_id =
          insurance.member_id || "";
        caseFormData.insurances_attributes[index].bin = insurance.bin || "";
        caseFormData.insurances_attributes[index].pcn = insurance.pcn || "";
        caseFormData.insurances_attributes[index].rx_group =
          insurance.rx_group || "";
        caseFormData.insurances_attributes[index].global_insurance_id =
          insurance.global_insurance_id
            ? insurance.global_insurance_id.toString()
            : null;
        caseFormData.insurances_attributes[index].is_primary_insurance =
          index === 0;
        caseFormData.insurances_attributes[index].insurance_priority = index;
      }
    });
  } else {
    caseFormData.insurances_attributes[0] = {
      global_insurance_id: null, insurance_priority: 0, is_primary_insurance: true,
      insurance_name: "",
      relation_to_patient: "self", policy: "",
      group_number: "", bin: "",
    }
  }

  return caseFormData;
}

export function populatingPrescriptionAttributes({ data }: CaseCloneResponseType): PrescriptionAttributesType {

  const prescription_attributes = {} as PrescriptionAttributesType;

  const drug_name = data.prescription.product.toLowerCase();
  if (data.prescription) {
    const diagnosis = data.prescription.diagnosis_code;
    prescription_attributes.diagnosis_code = diagnosis !== "" ? diagnosis.split(",").filter((code) => code.length > 0) : [];
    prescription_attributes.note = data.prescription.note || "";
    prescription_attributes.direction = data.prescription.direction || "";
    prescription_attributes.is_specialty_pharmacy_only = data.prescription.is_specialty_pharmacy_only !== null ? data.prescription.is_specialty_pharmacy_only : false;
    prescription_attributes.drug_name = drug_name;
    prescription_attributes.is_daw = format(new Date(), "MM/dd/yyyy")
    if (data.prescription.qty)
      prescription_attributes.qty = Number(data.prescription.qty)
  }

  if (data.prescription_attributes) {
    data.prescription_attributes.forEach((attr) => {
      switch(attr.name) {
        case "scheduled_date_of_service":
          let newSCheduledDate: string | undefined;
          if (attr.value) {
            if (attr.value.includes("T"))
              newSCheduledDate = attr.value.split("T")[0]
            else
              newSCheduledDate = attr.value
          }
          prescription_attributes.scheduled_date_of_service = newSCheduledDate;
          break;
        case "injection_site":
          prescription_attributes.injection_site = 
            attr.value ? (attr.value.includes("other") ? attr.value : `${attr.value[0].toUpperCase()}${attr.value.substring(1)} Knee`) : "";
          break;
        case "last_date_of_service":
         // console.log({ value: attr.value, label: drug_name === 'iovera' || drug_name === "iovera°" ? "last_surgical_date" : "last_date_of_service" })
          prescription_attributes[drug_name === 'iovera' || drug_name === "iovera°" ? "last_surgical_date" : "last_date_of_service"] =
            attr.value || "";
          break;
        case "last_surgical_date":
          prescription_attributes.last_surgical_date = attr.value || "";
          break;
        case "surgical_date":
          prescription_attributes.surgical_date = attr.value || "";
          break;
        case "treatment_date":
          prescription_attributes.treatment_date = attr.value || "";
          break;
        case "nerves_qty":
          prescription_attributes.nerves_qty = Number(attr.value) || 0;
          break;
        case "nerves":
          const splitValue = attr.value ? attr.value.split(",").filter((item) => item.length > 0) : [];
          prescription_attributes.nerves = splitValue;
          break;
        case "additional_nerves":
          prescription_attributes.other_nerves = attr.value || "";
          break;
        case "has_been_treated":
          prescription_attributes.has_been_treated = attr.value as "Yes" | "No" | "Unsure" | "";
          break;
        default:
          break;
      }
    })
  }
  //  if (data.patient_attributes && data.patient_attributes.length > 0) {
  //     if (data.patient_attributes.find((val) => val.name === "is_us_citizen"))
  //       prescription_attributes.is_us_citizen = data.patient_attributes.find((val) => val.name === "is_us_citizen")?.value === "true" ? true : false
  //     if (data.patient_attributes.find((val) => val.name === "is_enrolled_in_federal"))
  //       prescription_attributes.is_enrolled_in_federal = data.patient_attributes.find((val) => val.name === "is_enrolled_in_federal")?.value === "true" ? true : false
  //   }

  return prescription_attributes;
}
